import { useMemo } from "react";
import { trellisPalette } from "components/custom/analytics/palettes";
import type { Actions, TranscriptRecord } from "../types";
import { IoTime } from "react-icons/io5";

const ActionsBox = ({ transcriptData }: { transcriptData: TranscriptRecord[] }) => {
  const actions: Actions | undefined = useMemo(() => {
    const actionsItems = transcriptData.filter((item) => item.type === "actions");

    if (actionsItems.length === 0) {
      return undefined;
    }
    try {
      if (actionsItems[0]?.content) {
        return JSON.parse(actionsItems[0].content) as Actions;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }, [transcriptData]);

  const { DAActions, otherActions } = useMemo(() => {
    // Items can be plain strings or tuples: [text, time, role]
    type ActionItem = string | [text: string, time?: string, role?: string];

    const source = (actions?.["Actions for the Data Analyst"] ?? []) as ActionItem[];

    const da: ActionItem[] = [];
    const other: ActionItem[] = [];

    for (const item of source) {
      if (typeof item === "string") {
        // string items are considered non-DA and go to other
        other.push([item, "00:00:00", "UNKNOWN"]);
        continue;
      }

      const [text, time, role] = item;
      const normalizedRole = role ?? "UNKNOWN";

      if (normalizedRole.toUpperCase().includes("DA")) {
        // ensure tuple shape without mutating original
        da.push([text, time ?? "", normalizedRole]);
      } else {
        other.push([text, time ?? "", normalizedRole]);
      }
    }

    return { DAActions: da, otherActions: other };
  }, [actions]);

  const file_id = transcriptData[0]?.file_id;

  if (!actions) {
    return <div>No action items found.</div>;
  }

  return (
    <div className={`fs-standard`}>
      <span className={`fs-3 fw-bold`}>EA Action Items</span>
      <ul>
        {DAActions.length ?
          DAActions.map((action, index) => {
            let actionText = "";
            let timeValue = "";
            if (typeof action === "string") {
              actionText = action;
            } else {
              actionText = action[0];
              timeValue = action[1] ?? "";
            }
            return (
              <li key={index}>
                {actionText.includes(": ") ? actionText.split(": ")[1] : actionText}
                {timeValue && (
                  <span
                    className="ps-3"
                    style={{ color: trellisPalette[1], cursor: "pointer" }}
                    onClick={() => {
                      if (file_id) {
                        window.location.hash = `id=${file_id}&time=${timeValue}`;
                      }
                    }}
                  >
                    <IoTime /> {timeValue}
                  </span>
                )}
              </li>
            );
          })
        : <li>No action items found.</li>}
      </ul>

      <span className={`fs-3 fw-bold`}>Growth Action Items</span>
      <ul>
        {otherActions.length ?
          otherActions.map((action, index) => {
            let actionText = "";
            let timeValue = "";
            if (typeof action === "string") {
              actionText = action;
            } else {
              actionText = action[0];
              timeValue = action[1] ?? "";
            }
            return (
              <li key={index}>
                {actionText.includes(": ") ? actionText.split(": ")[1] : actionText}
                {timeValue && (
                  <span
                    className="ps-3"
                    style={{ color: trellisPalette[1], cursor: "pointer" }}
                    onClick={() => {
                      if (file_id) {
                        window.location.hash = `id=${file_id}&time=${timeValue}`;
                      }
                    }}
                  >
                    <IoTime /> {timeValue}
                  </span>
                )}
              </li>
            );
          })
        : <li>No action items found.</li>}
      </ul>

      <span className={`fs-3 fw-bold`}>Customer Action Items</span>
      <ul>
        {actions["Actions for the Customer"].length ?
          actions["Actions for the Customer"].map((action, index) => {
            let actionText = "";
            let timeValue = "";
            if (typeof action === "string") {
              actionText = action;
            } else {
              actionText = action[0];
              timeValue = action[1];
            }
            return (
              <li key={index}>
                {actionText.includes(": ") ? actionText.split(": ")[1] : actionText}
                {timeValue && (
                  <span
                    className="ps-3"
                    style={{ color: trellisPalette[1], cursor: "pointer" }}
                    onClick={() => {
                      if (file_id) {
                        window.location.hash = `id=${file_id}&time=${timeValue}`;
                      }
                    }}
                  >
                    <IoTime /> {timeValue}
                  </span>
                )}
              </li>
            );
          })
        : <li>No action items found.</li>}
      </ul>
    </div>
  );
};

export default ActionsBox;
